import React, { useState } from "react";
import { FaEye, FaEyeSlash, FaCheck, FaTimes } from "react-icons/fa";

interface PasswordInputProps {
  id: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  label: string;
  showStrengthIndicator?: boolean;
  disabled?: boolean;
}

interface PasswordRequirement {
  label: string;
  met: boolean;
}

const PasswordInput: React.FC<PasswordInputProps> = ({
  id,
  name,
  value,
  onChange,
  placeholder = "Enter password",
  label,
  showStrengthIndicator = true,
  disabled = false,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const requirements: PasswordRequirement[] = [
    { label: "At least 8 characters", met: value.length >= 8 },
    { label: "At least one letter", met: /[a-zA-Z]/.test(value) },
    { label: "At least one number", met: /\d/.test(value) },
  ];

  const metRequirements = requirements.filter((req) => req.met).length;
  const allRequirementsMet = metRequirements === requirements.length;

  return (
    <div>
      <label htmlFor={id} className="block mb-1 font-medium text-gray-700">
        {label} *
      </label>
      <div className="relative">
        <input
          id={id}
          type={showPassword ? "text" : "password"}
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          className="w-full border border-gray-300 rounded px-4 py-3 pr-12 focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
          required
          disabled={disabled}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
          tabIndex={-1}
        >
          {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
        </button>
      </div>

      {showStrengthIndicator && value.length > 0 && (
        <div className="mt-3">
          {/* Requirements Checklist */}
          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            <p className="text-xs font-semibold text-gray-600 mb-2">
              Password Requirements:
            </p>
            {requirements.map((req, index) => (
              <div
                key={index}
                className="flex items-center gap-2 text-sm transition-all"
              >
                {req.met ? (
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <FaCheck className="text-green-600 text-xs" />
                  </div>
                ) : (
                  <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <FaTimes className="text-gray-400 text-xs" />
                  </div>
                )}
                <span
                  className={
                    req.met ? "text-green-700 font-medium" : "text-gray-600"
                  }
                >
                  {req.label}
                </span>
              </div>
            ))}
          </div>

          {/* Success Message */}
          {allRequirementsMet && (
            <div className="mt-2 flex items-center gap-2 text-green-600 text-sm font-semibold">
              <FaCheck />
              <span>Strong password!</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PasswordInput;
